import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FaShoppingCart, FaHeart, FaRegHeart } from "react-icons/fa";
import api from '../api';
import { getCartFromSession } from "../utils/cartUtils";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { sectionCategory } from '../services/userProfile';
import { useSearch } from '../context/SearchContext';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { toast, ToastContainer } from "react-toastify";
import { getLocalData } from "../utils/localStorageHelper";
import "swiper/css";
import "swiper/css/navigation";

const ArrivalCart = ({ product, isLoggedIn }) => {
  const { sectionId, setSectionId } = useSearch();
  const [wishlist, setWishlist] = useState([]);
  const [wishlistedProducts, setWishlistedProducts] = useState({});
  const { wishlistCount, setWishlistCount } = useWishlist();
  const { addToCart, updateQuantity, removeFromCart, cart } = useCart();
  const [cartCount, setCartCount] = useState(0);
  const [arrivals, setArrival] = useState([]);
  const navigate = useNavigate();

  // ─── Cart Quantities ──────────────────────────────────────────────────────
  // Initialize SYNCHRONOUSLY from localStorage so on refresh the buttons
  // show correctly IMMEDIATELY — before any async cart context load.
  const [productQuantities, setProductQuantities] = useState(() => {
    try {
      const saved = localStorage.getItem("cartQuantities");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") return parsed;
      }
    } catch (e) { /* ignore */ }
    return {};
  });

  // Stable ref — always mirrors productQuantities so async handlers never
  // close over stale state
  const productQuantitiesRef = useRef(productQuantities);

  // Wrapper: every qty write goes through here so ref + localStorage stay in sync
  const updateProductQty = (updater) => {
    setProductQuantities((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      productQuantitiesRef.current = next;
      try { localStorage.setItem("cartQuantities", JSON.stringify(next)); } catch (e) { }
      return next;
    });
  };

  // Seed from cart context — only adds missing keys, never overwrites existing
  useEffect(() => {
    if (cart.length === 0) return;
    updateProductQty((prev) => {
      const next = { ...prev };
      const cartIdSet = new Set(cart.map((i) => i.id || i._id || i.productId));
      // Remove keys no longer in cart
      Object.keys(next).forEach((id) => {
        if (!cartIdSet.has(id)) delete next[id];
      });
      // Add missing keys from cart
      cart.forEach((item) => {
        const id = item.id || item._id || item.productId;
        if (id && next[id] === undefined) {
          next[id] = item.quantity || 1;
        }
      });
      return next;
    });
  }, [cart]);
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Wishlist — fetch on mount so heart icons show correct state ──────────
  useEffect(() => {
    const fetchWishlist = async () => {
      const storedData = localStorage.getItem("userData");
      const token = storedData ? JSON.parse(storedData).token : null;
      if (!token) return;
      try {
        const res = await api.get("/user/get-wishlists", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items =
          res.data?.data ||
          res.data?.wishlist ||
          res.data?.products ||
          [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach((item) => {
            const id = item._id || item?.product?._id;
            if (id) map[id] = true;
          });
          setWishlistedProducts(map);
          setWishlist(items);
          setWishlistCount(items.length);
        }
      } catch (err) {
        console.log("Failed to fetch wishlist", err);
      }
    };
    fetchWishlist();
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  const goToProductDetails = (product) => {
    var items = product;
    navigate(`/product-details/${product._id}`, { state: { items } });
  };

  useEffect(() => {
    if (!isLoggedIn) {
      const cart = getCartFromSession();
      const totalQty = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(totalQty);
    }
  }, [isLoggedIn]);

  const fetchCategory = async (sectionId) => {
    try {
      const res = await sectionCategory(sectionId);
      setSectionId(res.data);
      console.log("ThemCollection", res);
    } catch (error) {
      console.log("error", error.message);
    }
  }

  useEffect(() => {
    if (sectionId) {
      fetchCategory(sectionId);
    }
  }, [sectionId]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await api.get("/website/home_page");
        console.log("bestSellerA", res.data.data);
        setArrival(res.data.data.NewColection);
      } catch (err) {
        console.log("Failed to fetch section", err);
      }
    };
    fetchSections();
  }, []);

  // ─── Wishlist toggle (API-backed, fixed remove endpoint) ─────────────────
  const addWishList = async (product) => {
    console.log("itemproduct", product)
    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;

    if (!token) {
      setWishlist([]);
      setWishlistCount(0);
      setWishlistedProducts({});
      navigate("/user-login");
      return;
    }

    const isWishlisted = !!wishlistedProducts[product._id];

    try {
      // FIX: use correct endpoint for remove
      const endpoint = isWishlisted
        ? "/user/add-wishlist"
        : "/user/add-wishlist";

      await api.put(
        endpoint,
        { productId: product._id, product },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWishlistedProducts((prev) => ({
        ...prev,
        [product._id]: !isWishlisted,
      }));

      setWishlistCount((prev) =>
        !isWishlisted ? prev + 1 : Math.max(prev - 1, 0)
      );

      if (isWishlisted) {
        setWishlist((prev) => prev.filter((item) => item._id !== product._id));
        toast.info(`Removed from wishlist`, {
          position: "top-right",
          autoClose: 2000,
          theme: "dark",
        });
      } else {
        setWishlist((prev) => [...prev, product]);
        toast.success(`Added to wishlist ❤️`, {
          position: "top-right",
          autoClose: 2000,
          theme: "dark",
        });
      }
    } catch (err) {
      console.error("Error updating wishlist:", err);
      toast.error("Failed to update wishlist. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        theme: "dark",
      });
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Add to Cart ──────────────────────────────────────────────────────────
  const handleAddToCart = async (product) => {
    if (!product) {
      alert("Product not found!");
      return;
    }

    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;

    if (!token) {
      setWishlist([]);
      setWishlistCount(0);
      setWishlistedProducts({});
      navigate("/user-login");
      return;
    }

    const selectedColor = product?.variants?.[0]?.color?.name;
    const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
    const quantity = "1";

    const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

    if (selectedStock <= 0) {
      toast.warning("This Size is out of stock. Please Select another Size", {
        position: "top-right",
        autoClose: 2000,
        theme: "dark",
      });
      return;
    }

    await addToCart(product, selectedColor, selectedSize, quantity);

    // Immediately persist qty=1 so button switches to qty controls
    updateProductQty((prev) => ({ ...prev, [product._id]: 1 }));

    toast.success(`${quantity} Added to cart successfully`, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "dark",
    });
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Increase quantity (calls API + persists) ─────────────────────────────
  const handleIncreaseQuantity = async (product) => {
    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;
    if (!token) { navigate("/user-login"); return; }

    const currentQty = productQuantitiesRef.current[product._id] || 1;
    const maxStock = product?.variants?.[0]?.sizes?.[0]?.stock || 0;

    if(maxStock == 0){
        
    }

    if (currentQty >= maxStock) {
      toast.warning("Maximum stock limit reached", {
        position: "top-right",
        autoClose: 2000,
        theme: "dark",
      });
      return;
    }

    const newQty = currentQty + 1;
    // Update ref + state + localStorage BEFORE awaiting API
    updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
    await updateQuantity(
      product,
      product?.variants?.[0]?.color?.name,
      product?.variants?.[0]?.sizes?.[0]?.size,
      newQty
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Decrease quantity (calls API + persists) ─────────────────────────────
  const handleDecreaseQuantity = async (product) => {
    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;
    if (!token) { navigate("/user-login"); return; }

    const currentQty = productQuantitiesRef.current[product._id] || 1;
    const newQty = currentQty - 1;

    if (newQty <= 0) {
      // Remove from cart completely
      const cartItem = cart.find((item) => (item.id || item._id) === product._id);
      if (cartItem) {
        await removeFromCart(cartItem.id || cartItem._id);
      }
      updateProductQty((prev) => {
        const updated = { ...prev };
        delete updated[product._id];
        return updated;
      });
      return;
    }

    // Update ref + state + localStorage BEFORE awaiting API
    updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
    await updateQuantity(
      product,
      product?.variants?.[0]?.color?.name,
      product?.variants?.[0]?.sizes?.[0]?.size,
      newQty
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Buy Now ──────────────────────────────────────────────────────────────
  const handleBuyNow = (product) => {
    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;

    const selectedColor = product?.variants?.[0]?.color?.name;
    const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
    const quantity = "1";

    const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

    if (selectedStock <= 0) {
      toast.warning("This Size is out of stock. Please Select another Size", {
        position: "top-right",
        autoClose: 2000,
        theme: "dark",
      });
      return;
    }

    const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
    if (!selectedVariant) {
      console.error("Selected variant not found!");
      return;
    }

    const updatedProduct = {
      ...product,
      variant: selectedVariant,
      quantity: quantity,
    };

    if (!token) {
      setWishlist([]);
      setWishlistCount(0);
      setWishlistedProducts({});
      navigate("/user-login");
      return;
    }

    navigate("/check-out", {
      state: {
        token,
        products: updatedProduct,
        singleProduct: "yes",
        singleProductId: product._id,
      },
    });
  };
  // ─────────────────────────────────────────────────────────────────────────

  const getSelectedVariant = (product, selectedColor, selectedSize) => {

    if (!product?.variants) return null;

    // Find the color variant
    const colorVariant = product.variants.find(
      (v) => v.color.name === selectedColor
    );
    // console.log("varient data >>>>>",product.variants);
    // console.log("selectedColor data >>>>>",selectedColor);

    if (!colorVariant) return null;



    // Find the size variant inside that color
    const sizeVariant = colorVariant.sizes.find(
      (s) => s.size === selectedSize
    );
    if (!sizeVariant) return null;

    return {
      color: colorVariant.color,  // {name: 'Black', value: '#000000'}
      size: sizeVariant.size,     // 'M'
      originalPrice: sizeVariant.originalPrice,
      discountedPrice: sizeVariant.discountedPrice,
      stock: sizeVariant.stock,
      images: colorVariant.images, // optional, if needed
    };
  };

  return (
    <>
      <ToastContainer />
      <section
        className="product__section section--padding pt-0"
        id="arrival"
        style={{
          position: "relative",
          padding: "10px",
          color: "#000",
          overflow: "visible",
          backgroundImage: `
            linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 0%, rgba(255,255,255,0.87) 50%, rgba(255,255,255,1) 100%),
            url('/assets/img/ovaraybg.jpg')
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(20px)"
        }}
      >
        <div className="bubble bubble-1"></div>
        <div className="bubble bubble-2"></div>
        <div className="bubble bubble-3"></div>

        <div style={{ position: "relative", zIndex: 2 }}>
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-md-4 col-12"></div>
              <div className="col-md-4 col-12 text-center">
                <div
                  className="banner-wrapper"
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <div
                    className="banner-container"
                    style={{
                      display: "inline-block",
                      background: "#fff",
                      padding: "10px 15px",
                      border: "2.7px solid #e0b200",
                      borderTopRightRadius: "25px",
                      borderBottomLeftRadius: "25px",
                      borderTopLeftRadius: "0",
                      borderBottomRightRadius: "0",
                      position: "relative",
                      boxShadow: "0 0 15px rgba(255, 223, 100, 0.6)",
                      textAlign: "center",
                    }}
                  >
                    <span
                      className="--maintitle banner-text"
                      style={{
                        fontWeight: "bold",
                        color: "#000",
                        fontSize: "18px",
                        letterSpacing: "1px",
                      }}
                    >
                      NEW COLLECTIONS
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-4 col-12 text-md-end text-center mt-3 mt-md-0">
                <Link
                  to="/new-rivals?type=isNewCollection"
                  className="heartbeat-link"
                  style={{
                    textDecoration: "none",
                    color: "#ED2E2E",
                    fontWeight: "700",
                    fontSize: "16px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Explore All →
                </Link>
              </div>
            </div>
            <div className="container mt-4"></div>

            <div className="tab_content">
              <div id="featured" className="tab_pane active show">
                <div className="product__section--inner">
                  <Swiper
                    modules={[Navigation]}
                    navigation={{ clickable: true }}
                    spaceBetween={20}
                    slidesPerView={2.2}
                    slidesPerGroup={1} 
                    breakpoints={{
                      0: { slidesPerView: 1.08, slidesPerGroup: 1, spaceBetween: 10 },
                      480: { slidesPerView: 1.5, slidesPerGroup: 1, spaceBetween: 10 },
                      640: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 15 },
                      768: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 15 },
                      1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 20 },
                      1280: { slidesPerView: 5, slidesPerGroup: 1, spaceBetween: 20 },
                    }}
                    className="product__swiper arrivals-slider"
                  >
                    {arrivals.map((product) => {
                      const allOutOfStock = product.variants?.every((variant) =>
                        variant.sizes?.every((size) => size.stock === 0)
                      );

                      const selectedStockProduct = product?.variants?.[0]?.sizes?.[0]?.stock;

                      // Read from state (drives render) — ref used only in handlers
                      const isInCart = productQuantities.hasOwnProperty(product._id);
                      const qty = productQuantities[product._id] || 0;
                      const isWishlisted = !!wishlistedProducts[product._id];

                      return (
                        <SwiperSlide key={product._id}>
                          <div
                            className="product__items shadow-md overflow-hidden bg-white"
                            style={{
                              border: "3px solid #FFD700",
                              borderRadius: "6px",
                              height: "auto",
                              minHeight: "370px",
                              transition: "transform 0.3s ease, box-shadow 0.3s ease",
                              display: "flex",
                              flexDirection: "column",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-8px)";
                              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                            }}
                          >
                            {/* Image */}
                            <div
                              className="relative w-full"
                              style={{ maxWidth: "100%", margin: "0 auto" }}
                            >
                              <div
                                className='product-image-container'
                                style={{
                                  width: "210px",
                                  height: "200px",
                                  overflow: "hidden",
                                  position: "relative",
                                  borderRadius: "2px",
                                  cursor: "pointer",
                                }}
                                onClick={() => goToProductDetails(product)}
                              >
                                <img
                                  src={
                                    product.variants?.[0]?.images?.[0] ??
                                    "https://via.placeholder.com/300x300?text=No+Image"
                                  }
                                  alt={product.title || "Product"}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    display: "block",
                                  }}
                                />

                                {allOutOfStock && (
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: "10px",
                                      left: "10px",
                                      background: "rgba(255,255,255,0.9)",
                                      color: "red",
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                      padding: "3px 8px",
                                      borderRadius: "3px",
                                    }}
                                  >
                                    Out of Stock
                                  </span>
                                )}

                                {/* Wishlist button — fills red when wishlisted */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addWishList(product);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "10px",
                                    right: "10px",
                                    background: isWishlisted
                                      ? "rgba(237, 46, 46, 0.95)"
                                      : "#CEB256",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                    transition: "all 0.3s",
                                  }}
                                >
                                  {isWishlisted ? (
                                    <FaHeart style={{ color: "white", fontSize: "16px" }} />
                                  ) : (
                                    <FaRegHeart style={{ color: "white", fontSize: "16px" }} />
                                  )}
                                </button>

                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "8px",
                                    left: "8px",
                                    background: "rgba(0,0,0,0.6)",
                                    color: "white",
                                    padding: "3px 6px",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "3px",
                                  }}
                                >
                                  ⭐ {product.averageRating?.toFixed(1) || "0.0"}
                                </div>
                              </div>
                            </div>

                            {/* Content */}
                            <div
                              className="product-content-wrapper"
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                padding: "10px 8px",
                              }}
                            >
                              <div style={{ flex: 1, marginBottom: "8px" }}>
                                <div
                                  style={{
                                    // display: "flex",
                                    // alignItems: "center",
                                    // flexWrap: "wrap",
                                    // gap: "5px",
                                    // marginBottom: "8px",
                                  }}
                                >
                                  <span
                                    className="product-name"
                                    style={{
                                      display: "block",
                                      fontSize: "14px",
                                      fontWeight: "700",
                                      color: "#333",
                                      marginBottom: "4px",
                                      lineHeight: "1.3",
                                    }}
                                  >
                                    {product.name || product.title}
                                  </span>
                                  <span
                                    style={{
                                      display: "block",
                                      fontSize: "14px",
                                    }}
                                  >Size : {product.variants?.[0]?.sizes?.[0]?.size}, Color : {product.variants?.[0]?.color?.name}, Stock : {product.variants?.[0]?.sizes?.[0]?.stock}</span>
                                </div>
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#666",
                                    marginBottom: "6px",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {product.description.split(" ").length > 4
                                    ? product.description.split(" ").slice(0, 4).join(" ") + "..."
                                    : product.description}
                                </p>

                                {/* Prices */}
                                <div
                                  className="price-section"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "5px",
                                    marginBottom: "8px",
                                  }}
                                >
                                  {product.variants?.[0]?.sizes?.[0]?.discountedPrice && product.variants[0].sizes[0].discountedPrice != product.variants?.[0]?.sizes?.[0]?.originalPrice ? (
                                    <>
                                      <span style={{ color: "#28a745", fontWeight: "700", fontSize: "15px" }}>
                                        ₹{Number(product.variants[0].sizes[0].discountedPrice)}
                                      </span>
                                      <span style={{ color: "#999", fontSize: "13px", textDecoration: "line-through" }}>
                                        ₹{product.variants?.[0]?.sizes?.[0]?.originalPrice || 0}
                                      </span>
                                      {product.variants?.[0]?.sizes?.[0]?.discount && (
                                        <span style={{ color: "black", padding: "2px 6px", borderRadius: "3px", fontSize: "10px", fontWeight: "600" }}>
                                          {product.variants[0].sizes[0].discount}% OFF
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span style={{ color: "#28a745", fontWeight: "700", fontSize: "15px" }}>
                                      ₹{product.variants?.[0]?.sizes?.[0]?.originalPrice || 0}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Buttons */}
                              <div className="button-section" style={{ marginTop: "auto", paddingTop: "8px" }}>
                                {isInCart && !allOutOfStock && (selectedStockProduct > 0) ? (
                                  // ── Quantity controls ──
                                  <div
                                    style={{
                                      display: "flex",          // ✅ FIXED
                                      flexDirection: "row",
                                      gap: "8px",
                                      width: "100%",
                                      alignItems: "center"
                                    }}
                                  >
                                    {/* Quantity */}
                                    <div style={{ flex: 1 }} className="seller-card__qty-wrap">
                                      <button
                                        className="seller-card__qty-btn"
                                        onClick={(e) => { e.stopPropagation(); handleDecreaseQuantity(product); }}
                                      >
                                        −
                                      </button>

                                      <span className="seller-card__qty-display">{qty}</span>

                                      <button
                                        className="seller-card__qty-btn"
                                        onClick={(e) => { e.stopPropagation(); handleIncreaseQuantity(product); }}
                                      >
                                        +
                                      </button>
                                    </div>

                                    {/* Buy Now */}
                                    <button
                                      className="seller-card__btn"
                                      onClick={(e) => { e.stopPropagation(); handleBuyNow(product); }}
                                      style={{
                                        flex: 1,
                                        backgroundColor: "#28a745",
                                        padding: "8px",
                                        borderRadius: "5px",
                                        color: "#fff",
                                        border: "none",
                                        whiteSpace: "nowrap"
                                      }}
                                    >
                                      Buy Now
                                    </button>
                                  </div>
                                ) : (
                                  // ── Add to Cart + Buy Now ──
                                  <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddToCart(product);
                                      }}
                                      disabled={allOutOfStock}
                                      style={{
                                        flex: 1,
                                        padding: "8px 4px",
                                        backgroundColor: allOutOfStock ? "#ccc" : "#CEB256",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "5px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        cursor: allOutOfStock ? "not-allowed" : "pointer",
                                        transition: "all 0.3s ease",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Add to Cart
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBuyNow(product);
                                      }}
                                      disabled={allOutOfStock}
                                      style={{
                                        flex: 1,
                                        padding: "8px 4px",
                                        backgroundColor: allOutOfStock ? "#ccc" : "#28a745",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "5px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        cursor: allOutOfStock ? "not-allowed" : "pointer",
                                        transition: "all 0.3s ease",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Buy Now
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>
          {`
            .bubble {
              position: absolute;
              border-radius: 50%;
              opacity: 1;
              z-index: 0.1;
              animation: floatTriangle 5s infinite linear;
            }

            @keyframes floatTriangle {
              0% { transform: translate(0, 0); }
              33% { transform: translate(30px, -30px); }
              66% { transform: translate(-30px, -30px); }
              100% { transform: translate(0, 0); }
            }

            .bubble-1 { top: 6%; left: 15%; width: 100px; height: 100px; background: radial-gradient(circle,#E4F7FF, transparent); animation-delay: 1s; }
            .bubble-3 { top: 5%; left: 83%; width: 120px; height: 120px; background: radial-gradient(circle,#E4F7FF, transparent); animation-delay: 1s; }

            @media (max-width: 768px) {
              .bubble-3 { display: none; }
              .product__items { height: auto !important; }          /* ← was 380px */
              .product-image-container { height: 220px !important; }    /* ← taller now that full width */
              .product-content-wrapper { padding: 10px 8px !important; }
              .button-section {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
            }

            @keyframes float {
              0% { transform: translateY(0) scale(1); opacity: 0.4; }
              50% { transform: translateY(-20px) scale(1.05); opacity: 0.6; }
              100% { transform: translateY(0) scale(1); opacity: 0.4; }
            }
          `}
        </style>
      </section>
    </>
  )
}

export default ArrivalCart